/**
 * Scenario Data
 * Defines all available scenarios with their tasks, stakeholders, and context
 * Enhanced with company context and product sense challenges
 */

export const SCENARIO_DATA = {
  'saas-1': {
    id: 'saas-1',
    title: 'Discovery & Definition',
    track: 'SaaS',
    difficulty: 'Beginner',
    description: 'You\'re a new PM taking over from an outgoing PM. They\'ve left you with customer feedback and data. The CEO is pushing hard for a new feature launch and wants data to back up his request. Work with what you\'ve got and move quickly.',
    company: {
      name: 'FlowFinance',
      type: 'Fintech SaaS',
      stage: 'Series B',
      product: 'Expense management platform for SMBs',
      context: 'FlowFinance helps small businesses track expenses, manage receipts, and streamline accounting. You have 50,000 active customers and are growing 20% MoM. A competitor just launched AI-powered receipt categorization, and your CEO wants you to build something similar.',
      metrics: {
        users: '50,000',
        revenue: '$8M ARR',
        growth: '20% MoM',
        teamSize: '45 employees'
      },
      competitors: 'ReceiptBot (AI categorization), Expensify (enterprise focus), QuickBooks (accounting integration)',
      recentEvents: 'Raised $25M Series B in Q3. Competitor ReceiptBot launched AI categorization last month.'
    },
    currentSituation: 'You\'ve just been hired as a new PM to replace the outgoing PM. They\'ve left you with customer feedback surveys and user data they collected. The CEO is pushing hard for a new AI-powered expense categorization feature to launch quickly, and wants data to back up his request. You need to work with what you\'ve got and move fast.',
    timeline: 'Immediate - CEO wants to move quickly on this feature',
    constraints: 'Time pressure from CEO. Limited time to gather new data. Must work with existing customer feedback and metrics that the previous PM collected.',
    stakeholderKnowledge: {
      'leadership-ceo': {
        knows: ['Business strategy', 'Competitive landscape', 'Company priorities', 'Resource allocation', 'Market positioning'],
        channel: 'leadership'
      },
      'customer-success': {
        knows: ['User feedback', 'Feature requests', 'Support tickets', 'User pain points', 'Feature adoption rates'],
        channel: 'customer-success'
      },
      'data-analyst': {
        knows: ['Current metrics', 'User behavior data', 'Feature usage analytics', 'Conversion rates', 'Retention data'],
        channel: 'data'
      }
    },
    quickStart: {
      firstStep: 'Review the customer feedback and data that the previous PM collected. Blake has survey results ready to share, and Morgan has user metrics. Use this existing data to inform your decisions.',
      keyStakeholders: [
        { name: 'Blake (Customer Success)', reason: 'Has customer survey results from the previous PM that are ready to share' },
        { name: 'Morgan (Data Analyst)', reason: 'Has user metrics and data analysis from the previous PM that are ready to share' },
        { name: 'Jamie (CEO)', reason: 'Can clarify business priorities and competitive strategy' }
      ]
    },
    productBriefDoc: 'https://docs.google.com/document/d/17DICiQxP5GxeTOz2BMRWDvqjVRBVpi26pEClM3e_MfQ/edit?usp=sharing',
    productBriefInstructions: {
      summary: 'An editable, unfinished Product Brief (Google Doc) was started by the previous PM. To complete the scenario you must: save the doc as a copy, edit it to finish the brief, save it as a PDF, and upload that PDF here.',
      workflow: ['Open the Product Brief (link above)', 'Save a copy to your own Google Drive (File → Make a copy)', 'Edit the copy using insights from stakeholders, support tickets, user metrics, and survey results', 'Save/export your finished doc as a PDF (File → Download → PDF)', 'Upload the PDF in the Required Upload section to complete the scenario'],
      whatYouDefine: 'You define: user problems, a testable hypothesis statement, data insights driving the decision, the value proposition, the solution features, and success metrics of the solution. Use conversations with specific stakeholders, support tickets, user metrics, and user survey results to inform your brief.'
    },
    tasks: [
      {
        title: 'Set Assumptions & Game Plan',
        description: 'Use waypointing: state your assumptions (role, geographic focus, platform constraints) and articulate your game plan for discovery. Practice clear communication.',
        productSenseChallenge: 'Clear Communication - Waypointing and assumption setting',
        requiredArtifact: null
      },
      {
        title: 'Define Product Motivation',
        description: 'Articulate why this feature matters to users and the company. What problem are we solving? Why now?',
        productSenseChallenge: 'Product Motivation - Establish mission and purpose',
        requiredArtifact: null
      },
      {
        title: 'Segment Users',
        description: 'Identify different user segments (e.g., solopreneurs, small teams, accountants). Prioritize one segment based on reach and underserved needs.',
        productSenseChallenge: 'Segmentation - Define audience and prioritize target segment',
        requiredArtifact: null
      },
      {
        title: 'Map User Journey & Identify Problems',
        description: 'Map the user journey for your chosen persona. Identify specific pain points they experience with expense tracking. Prioritize by frequency and severity.',
        productSenseChallenge: 'Problem Identification - User journey mapping and pain point prioritization',
        requiredArtifact: null
      },
      {
        title: 'Set Success Metrics',
        description: 'Define how you\'ll measure success. What metrics matter? How will you know if this feature is working?',
        productSenseChallenge: 'Solution Development - Define success metrics',
        requiredArtifact: null
      },
      {
        title: 'Submit Product Brief',
        description: 'Complete the unfinished Product Brief started by the previous PM. Open the Google Doc (see Context tab), save a copy, edit it using insights from stakeholders (support tickets, user metrics, user survey results, current situation), then save as PDF and upload here. You define: user problems, a testable hypothesis statement, data insights driving the decision, the value proposition, solution features, and success metrics.',
        productSenseChallenge: 'Clear Communication - Synthesize findings into clear documentation',
        requiredArtifact: 'Product Brief',
        artifactDescription: 'One PDF: complete the Product Brief template (save doc as copy → edit → download as PDF → upload). Include: user problems, testable hypothesis, data insights, value proposition, solution features, success metrics.'
      }
    ],
    stakeholders: ['leadership-ceo', 'customer-success', 'data-analyst'],
    productSenseChallenges: [
      'Clear Communication: Waypointing, assumption setting, game plan articulation',
      'Product Motivation: Why this feature matters',
      'Segmentation: User segmentation and prioritization',
      'Problem Identification: User journey mapping, pain point prioritization',
      'Solution Development: Success metrics definition'
    ],
    successCriteria: {
      impact: 'Problem is well-defined and validated with data',
      feasibility: 'Solution is technically realistic',
      stakeholderManagement: 'All stakeholders are aligned',
      speed: 'Completed within reasonable timeframe',
      frameworkApplication: 'Used proper discovery frameworks'
    }
  },
  'saas-2': {
    id: 'saas-2',
    title: 'Agile Sprint Planning & Conflict Resolution',
    track: 'SaaS',
    difficulty: 'Beginner',
    description: 'Convert your product brief into actionable sprint work. Navigate conflicting priorities and align stakeholders with different perspectives.',
    company: {
      name: 'FlowFinance',
      type: 'Fintech SaaS',
      stage: 'Series B',
      product: 'Expense management platform for SMBs',
      context: 'You\'ve validated the AI receipt categorization feature. Now you need to break it down into workable pieces and get it into the sprint. However, engineering is pushing back on timelines, and marketing wants this rushed for a campaign.',
      metrics: {
        users: '50,000',
        revenue: '$8M ARR',
        growth: '20% MoM',
        teamSize: '45 employees'
      },
      competitors: 'ReceiptBot (AI categorization), Expensify (enterprise focus), QuickBooks (accounting integration)',
      recentEvents: 'Raised $25M Series B in Q3. Competitor ReceiptBot launched AI categorization last month. Marketing has announced a campaign that depends on this feature.'
    },
    currentSituation: 'You have a product brief approved by leadership for AI-powered receipt categorization. Now you need to break it down into user stories, get engineering estimates, and create a realistic sprint plan. However, tensions are emerging: Engineering says the timeline is too aggressive and they\'re already stretched thin. Marketing has committed to a campaign deadline. The VP of Product wants to balance quality with speed. You need to navigate these conflicts and find a path forward that works for everyone.',
    timeline: 'Sprint planning needs to be completed this week. Sprint starts next Monday. Marketing campaign launches in 6 weeks.',
    constraints: 'Engineering team is 60% allocated to other features. Backend team has 2 engineers available, frontend has 1. Marketing has made external commitments. Team is feeling burnt out.',
    stakeholderKnowledge: {
      'engineering-backend': {
        knows: ['Technical feasibility', 'API integration complexity', 'ML model requirements', 'System architecture', 'Timeline estimates', 'Team capacity concerns'],
        channel: 'engineering'
      },
      'engineering-frontend': {
        knows: ['UI/UX implementation', 'Design system components', 'User interaction patterns', 'Mobile responsiveness', 'Timeline estimates', 'Burnout concerns'],
        channel: 'engineering'
      },
      'leadership-vp': {
        knows: ['Product roadmap priorities', 'Resource allocation', 'Sprint capacity', 'Business goals', 'Team availability', 'Quality vs speed tradeoffs'],
        channel: 'leadership'
      },
      'customer-success': {
        knows: ['User feedback', 'Support tickets', 'Current customer pain points', 'Feature adoption concerns', 'Marketing campaign context'],
        channel: 'customer-success'
      }
    },
    quickStart: {
      firstStep: 'Start by understanding each stakeholder\'s perspective - engineering is concerned about timelines, while marketing needs this for a campaign',
      keyStakeholders: [
        { name: 'Alex (Backend Engineer)', reason: 'Is concerned about aggressive timelines and team capacity' },
        { name: 'Jordan (Frontend Engineer)', reason: 'Is worried about burnout and realistic estimates' },
        { name: 'Drew (VP Product)', reason: 'Needs to balance quality, speed, and team health' },
        { name: 'Blake (Customer Success)', reason: 'Can provide context on what customers actually need' }
      ]
    },
    tasks: [
      {
        title: 'Break Down Features',
        description: 'Convert features into user stories with clear acceptance criteria. Use INVEST principles.',
        productSenseChallenge: 'Solution Development - Feature breakdown',
        requiredArtifact: 'User Stories Document'
      },
      {
        title: 'Understand Stakeholder Concerns',
        description: 'Talk to each stakeholder individually. What are their concerns? What\'s negotiable vs. non-negotiable? Understand the root of the conflict.',
        productSenseChallenge: 'Stakeholder Management - Active listening and conflict identification',
        requiredArtifact: 'Stakeholder Perspectives Summary'
      },
      {
        title: 'Get Engineering Estimates',
        description: 'Work with engineers to get realistic estimates. They\'re pushing back on timelines - understand why and what\'s truly feasible.',
        productSenseChallenge: 'Stakeholder Management - Engineering collaboration under pressure',
        requiredArtifact: null
      },
      {
        title: 'Navigate Conflicting Priorities',
        description: 'Marketing wants speed, engineering wants quality, VP wants balance. Find common ground and propose compromises that address multiple concerns.',
        productSenseChallenge: 'Stakeholder Management - Conflict resolution and consensus building',
        requiredArtifact: 'Compromise Proposal'
      },
      {
        title: 'Prioritize Backlog with RICE',
        description: 'Use RICE framework (Reach, Impact, Confidence, Effort) to prioritize user stories. Explain your reasoning to align stakeholders.',
        productSenseChallenge: 'Solution Development - Prioritization framework application',
        requiredArtifact: 'Prioritized Backlog'
      },
      {
        title: 'Create Sprint Goals',
        description: 'Define sprint goals that balance competing priorities. Ensure team buy-in from both engineering and leadership.',
        productSenseChallenge: 'Clear Communication - Goal setting and stakeholder alignment',
        requiredArtifact: 'Sprint Goals Document'
      },
      {
        title: 'Set Definition of Done',
        description: 'Establish clear acceptance criteria and quality standards. Balance speed with quality given the constraints.',
        productSenseChallenge: 'Solution Development - Quality standards under pressure',
        requiredArtifact: null
      }
    ],
    stakeholders: ['engineering-backend', 'engineering-frontend', 'leadership-vp', 'customer-success'],
    productSenseChallenges: [
      'Solution Development: Feature breakdown and prioritization',
      'Stakeholder Management: Conflict resolution, active listening, and consensus building',
      'Framework Application: RICE prioritization',
      'Clear Communication: Balancing competing priorities and aligning diverse stakeholders'
    ],
    successCriteria: {
      impact: 'Sprint plan aligns with product goals and addresses stakeholder concerns',
      feasibility: 'Estimates are realistic and team feels heard',
      stakeholderManagement: 'Conflicts resolved and all stakeholders bought into the plan',
      speed: 'Planning completed efficiently despite tensions',
      frameworkApplication: 'Used proper prioritization and conflict resolution approaches'
    }
  },
  'saas-3': {
    id: 'saas-3',
    title: 'Design Sprint & Collaborative Workshops',
    track: 'SaaS',
    difficulty: 'Intermediate',
    description: 'Facilitate a rapid design sprint to solve the AI categorization UX challenge. Lead workshops, manage group dynamics, and drive to a testable solution.',
    company: {
      name: 'FlowFinance',
      type: 'Fintech SaaS',
      stage: 'Series B',
      product: 'Expense management platform for SMBs',
      context: 'User stories are ready, but the team is uncertain about the best UX approach for AI receipt categorization. Instead of guessing, you\'ll run a 3-day design sprint to rapidly prototype and validate the best solution.',
      metrics: {
        users: '50,000',
        revenue: '$8M ARR',
        growth: '20% MoM',
        teamSize: '45 employees'
      },
      competitors: 'ReceiptBot (AI categorization), Expensify (enterprise focus), QuickBooks (accounting integration)',
      recentEvents: 'Raised $25M Series B in Q3. Competitor ReceiptBot launched AI categorization last month.'
    },
    currentSituation: 'User stories are ready, but there are multiple possible UX approaches for AI receipt categorization. Rather than spending weeks debating, you\'re running a focused 3-day design sprint with cross-functional stakeholders. You need to facilitate workshops, help the team map the problem, generate and evaluate solutions, create a prototype, and test it with users. Engineering is skeptical of design sprints ("more meetings"), but design is excited. You need to keep everyone engaged and productive.',
    timeline: '3-day design sprint this week. User testing on Day 3. Development starts next week if prototype validates.',
    constraints: 'Only 3 days. Participants have limited availability. Design system must be followed. Engineering needs proof the approach works before committing. Mobile-first approach required.',
    stakeholderKnowledge: {
      'design-ux': {
        knows: ['User research findings', 'User journey maps', 'Usability testing results', 'User pain points', 'Accessibility requirements', 'Design sprint methodology'],
        channel: 'design'
      },
      'design-ui': {
        knows: ['Design system components', 'Visual design patterns', 'Responsive breakpoints', 'Rapid prototyping techniques', 'Design timeline', 'Implementation feasibility'],
        channel: 'design'
      },
      'engineering-frontend': {
        knows: ['Component library availability', 'Technical constraints', 'Implementation complexity', 'Timeline estimates', 'Mobile considerations', 'Skepticism about design sprints'],
        channel: 'engineering'
      },
      'customer-success': {
        knows: ['User workflows', 'Real customer stories', 'Current workarounds', 'Pain point severity', 'Feature adoption concerns'],
        channel: 'customer-success'
      }
    },
    quickStart: {
      firstStep: 'Start by preparing the design sprint - define the problem statement, set the agenda, and align participants on goals',
      keyStakeholders: [
        { name: 'Riley (UX Designer)', reason: 'Has user research and will co-facilitate the sprint' },
        { name: 'Casey (UI Designer)', reason: 'Will create rapid prototypes during the sprint' },
        { name: 'Jordan (Frontend Engineer)', reason: 'Can assess technical feasibility and keep solutions realistic' },
        { name: 'Blake (Customer Success)', reason: 'Can participate and share customer insights' }
      ]
    },
    tasks: [
      {
        title: 'Prepare Design Sprint',
        description: 'Set up the 3-day sprint: define clear problem statement, create agenda, recruit participants, prepare materials. What does success look like?',
        productSenseChallenge: 'Clear Communication - Workshop planning and goal setting',
        requiredArtifact: 'Design Sprint Plan'
      },
      {
        title: 'Facilitate Day 1: Map & Understand',
        description: 'Lead exercises to map the user journey, review research insights, and align on the problem. Keep the team engaged and productive. Manage different perspectives.',
        productSenseChallenge: 'Stakeholder Management - Workshop facilitation and group dynamics',
        requiredArtifact: 'Journey Map & Problem Definition'
      },
      {
        title: 'Facilitate Day 2: Sketch & Decide',
        description: 'Guide rapid ideation and solution sketching. Help the team evaluate ideas and make decisions (even when people disagree). Navigate to a chosen direction.',
        productSenseChallenge: 'Solution Development - Collaborative decision making and ideation',
        requiredArtifact: 'Solution Sketches & Decision'
      },
      {
        title: 'Facilitate Day 3: Prototype & Test',
        description: 'Coordinate rapid prototyping. Keep team focused on what\'s testable. Run user testing sessions and gather feedback. Synthesize learnings.',
        productSenseChallenge: 'Problem Identification - Rapid prototyping and user validation',
        requiredArtifact: 'Prototype & User Testing Results'
      },
      {
        title: 'Balance User Needs vs Constraints',
        description: 'Throughout the sprint, help the team find solutions that are valuable, feasible, and within constraints. Make trade-offs collaboratively.',
        productSenseChallenge: 'Stakeholder Management - Cross-functional collaboration under time pressure',
        requiredArtifact: 'Trade-off Analysis Document'
      },
      {
        title: 'Synthesize & Present Recommendations',
        description: 'Package sprint results into clear recommendations. What did you learn? What should you build? Present to stakeholders with evidence.',
        productSenseChallenge: 'Clear Communication - Workshop synthesis and decision communication',
        requiredArtifact: 'Sprint Results & Recommendations'
      }
    ],
    stakeholders: ['design-ux', 'design-ui', 'engineering-frontend', 'customer-success'],
    productSenseChallenges: [
      'Clear Communication: Workshop planning, facilitation, and synthesis',
      'Stakeholder Management: Managing group dynamics and cross-functional collaboration',
      'Solution Development: Rapid ideation and collaborative decision making',
      'Problem Identification: User validation and rapid prototyping'
    ],
    successCriteria: {
      impact: 'Sprint produces validated, user-centered solution',
      feasibility: 'Design is technically feasible and realistic',
      stakeholderManagement: 'Team is aligned, engaged, and bought into the approach',
      speed: 'Sprint completed on time with actionable learnings',
      frameworkApplication: 'Applied design sprint methodology effectively'
    }
  },
  'saas-4': {
    id: 'saas-4',
    title: 'Development Sprint & GTM Planning',
    track: 'SaaS',
    difficulty: 'Intermediate',
    description: 'Feature is in development. Manage daily progress, unblock engineers, and simultaneously plan the go-to-market strategy and launch.',
    company: {
      name: 'FlowFinance',
      type: 'Fintech SaaS',
      stage: 'Series B',
      product: 'Expense management platform for SMBs',
      context: 'Development has started on AI receipt categorization. While managing the sprint, you also need to coordinate go-to-market planning with marketing, sales, and customer success.',
      metrics: {
        users: '50,000',
        revenue: '$8M ARR',
        growth: '20% MoM',
        teamSize: '45 employees'
      },
      competitors: 'ReceiptBot (AI categorization), Expensify (enterprise focus), QuickBooks (accounting integration)',
      recentEvents: 'Raised $25M Series B in Q3. Competitor ReceiptBot launched AI categorization last month. CEO wants this to be a signature product launch.'
    },
    currentSituation: 'Development has started on AI receipt categorization, and you\'re running daily standups. Engineers are making progress but encountering issues with the ML API integration. Simultaneously, you need to plan the go-to-market strategy: marketing wants positioning and messaging, sales needs enablement materials, customer success needs support documentation, and leadership wants a rollout plan. You\'re juggling development issues while coordinating launch preparation across multiple teams.',
    timeline: 'Feature needs to launch in 3 weeks. Sprint ends in 2 weeks. GTM plan needed in 1 week.',
    constraints: 'ML API has rate limits. Backend team is also working on other features. Budget for API calls is limited. Marketing has limited budget. Sales needs 2 weeks for enablement. Customer success is already overwhelmed.',
    stakeholderKnowledge: {
      'engineering-backend': {
        knows: ['ML API integration status', 'Rate limit constraints', 'Technical blockers', 'Progress updates', 'Timeline risks'],
        channel: 'engineering'
      },
      'engineering-frontend': {
        knows: ['UI implementation progress', 'Component development status', 'Design implementation issues', 'Timeline estimates'],
        channel: 'engineering'
      },
      'leadership-vp': {
        knows: ['Sprint capacity', 'Resource allocation', 'Business priorities', 'Launch timeline expectations', 'Budget constraints', 'GTM strategy'],
        channel: 'leadership'
      },
      'customer-success': {
        knows: ['Support documentation needs', 'User communication requirements', 'Training needs', 'Adoption challenges', 'Current support load'],
        channel: 'customer-success'
      }
    },
    quickStart: {
      firstStep: 'Start by checking in with Engineering to understand current progress, then coordinate with customer success on launch preparation',
      keyStakeholders: [
        { name: 'Alex (Backend Engineer)', reason: 'Can explain ML API integration issues and blockers' },
        { name: 'Jordan (Frontend Engineer)', reason: 'Can provide UI implementation status' },
        { name: 'Drew (VP Product)', reason: 'Can help prioritize and coordinate GTM planning' },
        { name: 'Blake (Customer Success)', reason: 'Needs to prepare for feature launch and user communication' }
      ]
    },
    tasks: [
      {
        title: 'Run Daily Standups',
        description: 'Facilitate async standups and track progress. Use waypointing to structure updates.',
        productSenseChallenge: 'Clear Communication - Structured standups',
        requiredArtifact: null
      },
      {
        title: 'Unblock Engineers',
        description: 'Answer questions, clarify requirements, and remove blockers. The ML API has rate limits - how do we handle this?',
        productSenseChallenge: 'Stakeholder Management - Engineering support',
        requiredArtifact: null
      },
      {
        title: 'Define Launch Strategy',
        description: 'Decide on rollout approach: gradual rollout or full launch? Which user segments first? What\'s the rollback plan if things go wrong?',
        productSenseChallenge: 'Solution Development - Launch strategy and risk management',
        requiredArtifact: 'Launch Strategy Document'
      },
      {
        title: 'Create GTM Plan',
        description: 'Coordinate launch preparation: What messaging? What enablement materials? Who owns what? Work with customer success on user communication.',
        productSenseChallenge: 'Stakeholder Management - Cross-functional GTM coordination',
        requiredArtifact: 'Go-to-Market Plan'
      },
      {
        title: 'Develop Enablement Materials',
        description: 'Create support documentation, user guides, and communication templates. Help customer success prepare for launch.',
        productSenseChallenge: 'Clear Communication - Multi-audience enablement',
        requiredArtifact: 'Enablement Package'
      },
      {
        title: 'Make Trade-off Decisions',
        description: 'When scope changes or issues arise, make informed decisions. Should we reduce accuracy to meet deadline? Impact on GTM timeline?',
        productSenseChallenge: 'Solution Development - Trade-off analysis',
        requiredArtifact: 'Trade-off Decision Document'
      },
      {
        title: 'Manage Stakeholder Expectations',
        description: 'Keep leadership and launch teams informed of progress. Communicate delays clearly and adjust GTM timeline if needed.',
        productSenseChallenge: 'Clear Communication - Stakeholder updates and timeline coordination',
        requiredArtifact: 'Stakeholder Update Document'
      },
      {
        title: 'Ensure Quality',
        description: 'Make sure the feature meets acceptance criteria before launch. Balance speed with quality. Launch readiness checklist.',
        productSenseChallenge: 'Solution Development - Quality assurance and launch readiness',
        requiredArtifact: 'Launch Readiness Checklist'
      }
    ],
    stakeholders: ['engineering-backend', 'engineering-frontend', 'leadership-vp', 'customer-success'],
    productSenseChallenges: [
      'Clear Communication: Structured updates, waypointing, and multi-audience enablement',
      'Stakeholder Management: Engineering support, GTM coordination, and cross-functional collaboration',
      'Solution Development: Trade-off decisions, launch strategy, and risk management'
    ],
    successCriteria: {
      impact: 'Feature delivered with quality and launch well-planned',
      feasibility: 'Technical challenges handled and GTM plan is realistic',
      stakeholderManagement: 'Engineering unblocked and launch teams prepared',
      speed: 'Feature and GTM plan delivered on time',
      frameworkApplication: 'Applied agile and GTM best practices'
    }
  },
  'saas-5': {
    id: 'saas-5',
    title: 'Launch Analysis & Monetization Strategy',
    track: 'SaaS',
    difficulty: 'Advanced',
    description: 'Feature has launched successfully. Analyze results, gather feedback, and determine pricing/monetization strategy to capture value.',
    company: {
      name: 'FlowFinance',
      type: 'Fintech SaaS',
      stage: 'Series B',
      product: 'Expense management platform for SMBs',
      context: 'AI receipt categorization has launched and users love it! 60% adoption and high satisfaction. But it costs $15K/month to run and leadership wants to know: how will we monetize this? Should we charge for it?',
      metrics: {
        users: '50,000',
        revenue: '$8M ARR',
        growth: '20% MoM',
        teamSize: '45 employees'
      },
      competitors: 'ReceiptBot (charges $5/user/month for AI), Expensify (includes AI in enterprise plans), QuickBooks (no AI features yet)',
      recentEvents: 'Raised $25M Series B in Q3. AI feature launched last month with 60% adoption. CFO is asking about monetization. Board wants path to profitability.'
    },
    currentSituation: 'Your AI receipt categorization feature has launched and is a hit! 60% adoption, NPS of 65, users saving 20+ minutes per week. However, it costs $15K/month in API fees (growing with usage) and you launched it free to all users. The CFO is asking how you\'ll monetize it. The CEO is concerned about upsetting customers if you start charging. Marketing worries it will hurt acquisition. You need to analyze the data, understand customer willingness to pay, design a pricing strategy, and make a recommendation that balances revenue, growth, and customer satisfaction.',
    timeline: 'Initial analysis this week. Pricing recommendation in 2 weeks. Decision on monetization by end of month.',
    constraints: 'Current customers expect it free. Engineering capacity is limited. CFO wants to cover costs at minimum. Marketing worried about churn. Competitors charge differently.',
    stakeholderKnowledge: {
      'data-analyst': {
        knows: ['Feature usage metrics', 'Adoption rates', 'User behavior data', 'Cost per user', 'Usage patterns by segment'],
        channel: 'data'
      },
      'data-scientist': {
        knows: ['Model performance data', 'Cost optimization options', 'Statistical significance', 'Usage forecasting'],
        channel: 'data'
      },
      'customer-success': {
        knows: ['User feedback', 'Value perception', 'Feature importance', 'Willingness to pay signals', 'Churn risk assessment'],
        channel: 'customer-success'
      },
      'leadership-ceo': {
        knows: ['Business strategy', 'Growth vs profitability tradeoffs', 'Competitive positioning', 'Board expectations', 'Risk tolerance on pricing'],
        channel: 'leadership'
      },
      'leadership-vp': {
        knows: ['Product strategy', 'Pricing philosophy', 'Customer segmentation', 'Competitive pricing models', 'Long-term roadmap impact'],
        channel: 'leadership'
      }
    },
    quickStart: {
      firstStep: 'Start by understanding the business context and cost structure from leadership, then analyze usage data',
      keyStakeholders: [
        { name: 'Jamie (CEO)', reason: 'Can clarify strategic priorities and risk tolerance on pricing changes' },
        { name: 'Drew (VP Product)', reason: 'Can provide context on pricing strategy and product philosophy' },
        { name: 'Morgan (Data Analyst)', reason: 'Has usage data, costs, and segmentation analysis' },
        { name: 'Blake (Customer Success)', reason: 'Can assess customer value perception and churn risk' }
      ]
    },
    tasks: [
      {
        title: 'Analyze Launch Performance',
        description: 'Review adoption metrics, user satisfaction, and feature impact. The feature is successful, but what\'s it costing us?',
        productSenseChallenge: 'Solution Development - Launch metrics analysis',
        requiredArtifact: 'Launch Performance Report'
      },
      {
        title: 'Segment Users by Value',
        description: 'Not all users get equal value from AI. Who uses it most? Who would pay for it? Segment by usage, company size, and use case.',
        productSenseChallenge: 'Segmentation - Value-based customer segmentation',
        requiredArtifact: 'Customer Value Segmentation'
      },
      {
        title: 'Analyze Competitive Pricing',
        description: 'Study how competitors price AI features. What can you learn from their models? What works in this market?',
        productSenseChallenge: 'Solution Development - Competitive pricing analysis',
        requiredArtifact: 'Competitive Pricing Analysis'
      },
      {
        title: 'Assess Willingness to Pay',
        description: 'Talk to customers through Blake. Would they pay for this? How much? What value do they perceive? Survey or interview users.',
        productSenseChallenge: 'Problem Identification - Value perception validation',
        requiredArtifact: 'Willingness to Pay Research'
      },
      {
        title: 'Design Pricing Options',
        description: 'Create 3-4 pricing models: per-user add-on? Usage-based? Premium tier? Grandfather existing users? Model revenue impact.',
        productSenseChallenge: 'Solution Development - Pricing model design and financial modeling',
        requiredArtifact: 'Pricing Model Options & Financial Projections'
      },
      {
        title: 'Develop Rollout Strategy',
        description: 'How do you communicate pricing changes? Minimize churn while capturing value. Change management for monetization.',
        productSenseChallenge: 'Stakeholder Management - Pricing change management',
        requiredArtifact: 'Pricing Rollout & Communication Plan'
      },
      {
        title: 'Make Pricing Recommendation',
        description: 'Synthesize analysis into clear recommendation. What pricing model? Why? What are risks? How do you mitigate them?',
        productSenseChallenge: 'Clear Communication - Strategic pricing recommendation',
        requiredArtifact: 'Pricing Strategy Recommendation'
      }
    ],
    stakeholders: ['data-analyst', 'data-scientist', 'customer-success', 'leadership-ceo', 'leadership-vp'],
    productSenseChallenges: [
      'Solution Development: Launch analysis, competitive pricing analysis, and financial modeling',
      'Segmentation: Value-based customer segmentation',
      'Problem Identification: Value perception validation and willingness to pay research',
      'Stakeholder Management: Pricing change management and strategic communication',
      'Clear Communication: Strategic pricing recommendation with risk mitigation'
    ],
    successCriteria: {
      impact: 'Pricing strategy captures value while maintaining growth',
      feasibility: 'Pricing model is financially sound and operationally realistic',
      stakeholderManagement: 'Leadership aligned and customer impact minimized',
      speed: 'Analysis and recommendation completed thoroughly',
      frameworkApplication: 'Applied pricing, segmentation, and monetization frameworks'
    }
  },
  'ai-1': {
    id: 'ai-1',
    title: 'AI Feature Discovery',
    track: 'AI',
    difficulty: 'Intermediate',
    description: 'Explore adding AI/ML capabilities to your product. Identify use cases and work with ML engineers.',
    company: {
      name: 'TaskFlow',
      type: 'Productivity SaaS',
      stage: 'Series A',
      product: 'Project management tool for remote teams',
      context: 'TaskFlow helps remote teams collaborate on projects. You have 20,000 users and are looking to differentiate with AI. Leadership wants to explore AI capabilities but isn\'t sure where to start.',
      metrics: {
        users: '20,000',
        revenue: '$3M ARR',
        growth: '15% MoM',
        teamSize: '28 employees'
      },
      competitors: 'Asana (established player), Monday.com (visual workflows), Notion (all-in-one workspace)',
      recentEvents: 'Raised $12M Series A in Q2. Market is seeing increased demand for AI-powered productivity tools.'
    },
    currentSituation: 'Leadership is interested in adding AI capabilities to differentiate TaskFlow. You need to identify valuable use cases, understand technical feasibility, and define success metrics for AI features. The market is buzzing about AI, but you want to build something that actually adds value, not just follow trends.',
    timeline: 'AI use case discovery needs to be completed in 2 weeks. Technical feasibility review in 3 weeks.',
    constraints: 'Limited ML engineering resources. Budget for AI infrastructure is constrained. Need to prove value quickly.',
    stakeholderKnowledge: {
      'engineering-ml': {
        knows: ['AI model capabilities', 'Technical feasibility', 'Infrastructure requirements', 'Cost estimates', 'Timeline constraints'],
        channel: 'engineering'
      },
      'data-scientist': {
        knows: ['Data requirements', 'Model performance expectations', 'Statistical approaches', 'Evaluation metrics'],
        channel: 'data'
      },
      'leadership-vp': {
        knows: ['Strategic priorities', 'Market positioning', 'Resource allocation', 'Competitive landscape', 'Business goals'],
        channel: 'leadership'
      }
    },
    quickStart: {
      firstStep: 'Start by talking to ML Engineering to understand what\'s technically feasible with current resources',
      keyStakeholders: [
        { name: 'Sam (ML Engineer)', reason: 'Can explain AI capabilities and technical constraints' },
        { name: 'Taylor (Data Scientist)', reason: 'Can help define data requirements and success metrics' },
        { name: 'Drew (VP Product)', reason: 'Can clarify strategic priorities and resource allocation' }
      ]
    },
    tasks: [
      {
        title: 'Set Assumptions & Game Plan',
        description: 'State your assumptions about the AI feature space and articulate your discovery approach.',
        productSenseChallenge: 'Clear Communication - Waypointing for AI discovery'
      },
      {
        title: 'Identify AI Use Cases',
        description: 'Brainstorm AI use cases that add real value. Consider: task prioritization, meeting summaries, smart suggestions. Prioritize by user value.',
        productSenseChallenge: 'Solution Development - AI use case brainstorming and prioritization'
      },
      {
        title: 'Segment Users for AI Features',
        description: 'Which user segments would benefit most from AI? Power users? New users? Teams vs individuals?',
        productSenseChallenge: 'Segmentation - AI feature targeting'
      },
      {
        title: 'Assess Technical Feasibility',
        description: 'Work with ML engineers to understand what\'s possible. What are the constraints? Cost? Latency?',
        productSenseChallenge: 'Stakeholder Management - ML engineering collaboration'
      },
      {
        title: 'Define AI Success Metrics',
        description: 'How will you measure AI feature performance? Accuracy? User satisfaction? Time saved?',
        productSenseChallenge: 'Solution Development - AI metrics definition'
      },
      {
        title: 'Consider Ethical Implications',
        description: 'Think about bias, privacy, and ethical concerns. How do we ensure AI is fair and transparent?',
        productSenseChallenge: 'Product Motivation - Ethical considerations'
      }
    ],
    stakeholders: ['engineering-ml', 'data-scientist', 'leadership-vp'],
    productSenseChallenges: [
      'Clear Communication: Waypointing for AI discovery',
      'Solution Development: AI use case brainstorming and prioritization',
      'Segmentation: AI feature targeting',
      'Stakeholder Management: ML engineering collaboration',
      'Product Motivation: Ethical considerations'
    ],
    successCriteria: {
      impact: 'AI use case adds real value',
      feasibility: 'Technically feasible with available resources',
      stakeholderManagement: 'ML team is aligned',
      speed: 'Discovery completed efficiently',
      frameworkApplication: 'Applied AI product best practices'
    }
  },
  'ai-2': {
    id: 'ai-2',
    title: 'Prompt Engineering & LLM Integration',
    track: 'AI',
    difficulty: 'Advanced',
    description: 'Integrate generative AI into your product. Design prompts, safety measures, and user experience.',
    company: {
      name: 'TaskFlow',
      type: 'Productivity SaaS',
      stage: 'Series A',
      product: 'Project management tool for remote teams',
      context: 'You\'ve decided to add an AI meeting summary feature using LLMs. This will help teams capture action items from async video calls.',
      metrics: {
        users: '20,000',
        revenue: '$3M ARR',
        growth: '15% MoM',
        teamSize: '28 employees'
      },
      competitors: 'Asana (established player), Monday.com (visual workflows), Notion (all-in-one workspace)',
      recentEvents: 'Raised $12M Series A in Q2. Market is seeing increased demand for AI-powered productivity tools.'
    },
    currentSituation: 'You\'ve decided to add an AI meeting summary feature using LLMs. This will automatically generate summaries and action items from recorded video calls. You need to design prompt templates, implement safety guardrails, and create a user experience that handles AI uncertainty gracefully. The feature needs to work in 10+ languages.',
    timeline: 'Prompt design and testing needs to be completed in 2 weeks. Integration in 4 weeks.',
    constraints: 'LLM API costs are significant. Need to optimize for cost while maintaining quality. Multi-language support adds complexity.',
    stakeholderKnowledge: {
      'engineering-ml': {
        knows: ['LLM API capabilities', 'Prompt engineering techniques', 'Cost optimization strategies', 'Multi-language support', 'Safety guardrails'],
        channel: 'engineering'
      },
      'design-ux': {
        knows: ['User experience patterns for AI', 'Handling AI uncertainty in UI', 'Trust and transparency design', 'Error state design'],
        channel: 'design'
      },
      'data-scientist': {
        knows: ['Model evaluation metrics', 'Performance benchmarks', 'Quality assessment methods', 'Statistical validation'],
        channel: 'data'
      }
    },
    quickStart: {
      firstStep: 'Start by talking to ML Engineering to understand LLM capabilities and prompt design constraints',
      keyStakeholders: [
        { name: 'Sam (ML Engineer)', reason: 'Can explain LLM capabilities and prompt engineering best practices' },
        { name: 'Riley (UX Designer)', reason: 'Can help design UX that handles AI uncertainty gracefully' },
        { name: 'Taylor (Data Scientist)', reason: 'Can help define evaluation metrics and quality standards' }
      ]
    },
    tasks: [
      {
        title: 'Design Prompt Templates',
        description: 'Work with AI team to create effective prompts. Test different prompt structures. How do we ensure consistent output?',
        productSenseChallenge: 'Solution Development - Prompt engineering'
      },
      {
        title: 'Define Safety Guardrails',
        description: 'Implement measures to prevent harmful outputs. What content should be filtered? How do we handle errors?',
        productSenseChallenge: 'Product Motivation - Safety and trust'
      },
      {
        title: 'Design UX for AI Uncertainty',
        description: 'Create experiences that handle AI limitations gracefully. How do we show confidence? Handle hallucinations?',
        productSenseChallenge: 'Problem Identification - AI UX challenges'
      },
      {
        title: 'Segment by Use Case',
        description: 'Different teams use meetings differently. How do we tailor the AI output for different segments?',
        productSenseChallenge: 'Segmentation - AI feature personalization'
      },
      {
        title: 'Plan Feedback Loops',
        description: 'Design systems to improve AI performance over time. How do we collect user feedback? Retrain models?',
        productSenseChallenge: 'Solution Development - Continuous improvement'
      },
      {
        title: 'Manage Costs',
        description: 'Balance AI capabilities with API costs. What\'s the cost per summary? How do we optimize?',
        productSenseChallenge: 'Solution Development - Cost optimization'
      }
    ],
    stakeholders: ['engineering-ml', 'design-ux', 'data-scientist'],
    productSenseChallenges: [
      'Solution Development: Prompt engineering and optimization',
      'Product Motivation: Safety and trust',
      'Problem Identification: AI UX challenges',
      'Segmentation: AI feature personalization',
      'Solution Development: Continuous improvement and cost optimization'
    ],
    successCriteria: {
      impact: 'AI feature provides value to users',
      feasibility: 'Prompts and guardrails work effectively',
      stakeholderManagement: 'Team understands AI limitations',
      speed: 'Integration completed efficiently',
      frameworkApplication: 'Applied prompt engineering best practices'
    }
  },
  'ai-3': {
    id: 'ai-3',
    title: 'AI Model Performance Management',
    track: 'AI',
    difficulty: 'Advanced',
    description: 'AI feature is underperforming. Analyze metrics, prioritize improvements, and communicate limitations.',
    company: {
      name: 'TaskFlow',
      type: 'Productivity SaaS',
      stage: 'Series A',
      product: 'Project management tool for remote teams',
      context: 'Your AI meeting summary feature has been live for 3 months. Performance metrics show it\'s not meeting targets.',
      metrics: {
        users: '20,000',
        revenue: '$3M ARR',
        growth: '15% MoM',
        teamSize: '28 employees'
      },
      competitors: 'Asana (established player), Monday.com (visual workflows), Notion (all-in-one workspace)',
      recentEvents: 'Raised $12M Series A in Q2. Market is seeing increased demand for AI-powered productivity tools.'
    },
    currentSituation: 'Your AI meeting summary feature has been live for 3 months, but performance metrics show it\'s not meeting targets. Accuracy is 75% (target was 90%), user satisfaction is declining, and costs are higher than expected. You need to analyze what\'s wrong, prioritize improvements, and communicate technical limitations to non-technical stakeholders. The CEO is asking tough questions.',
    timeline: 'Analysis and improvement plan needed this week. Implementation decisions by end of month.',
    constraints: 'Limited ML engineering resources. API costs are 30% over budget. Need to improve performance without increasing costs significantly.',
    stakeholderKnowledge: {
      'data-scientist': {
        knows: ['Model performance metrics', 'Accuracy breakdown by segment', 'Statistical analysis', 'Root cause hypotheses', 'Improvement opportunities'],
        channel: 'data'
      },
      'engineering-ml': {
        knows: ['Technical limitations', 'Model architecture constraints', 'Cost optimization options', 'Improvement feasibility', 'Timeline estimates'],
        channel: 'engineering'
      },
      'data-analyst': {
        knows: ['User satisfaction metrics', 'Feature usage data', 'Cost per summary', 'Adoption patterns', 'Churn impact'],
        channel: 'data'
      },
      'leadership-vp': {
        knows: ['Business impact', 'Resource constraints', 'Strategic priorities', 'Budget limitations', 'Competitive pressure'],
        channel: 'leadership'
      }
    },
    quickStart: {
      firstStep: 'Start by talking to Data Science team to understand the performance metrics and root causes',
      keyStakeholders: [
        { name: 'Taylor (Data Scientist)', reason: 'Can explain model performance and identify root causes' },
        { name: 'Sam (ML Engineer)', reason: 'Can assess technical improvement options and feasibility' },
        { name: 'Morgan (Data Analyst)', reason: 'Can provide user satisfaction and cost metrics' },
        { name: 'Drew (VP Product)', reason: 'Can help prioritize improvements within resource constraints' }
      ]
    },
    tasks: [
      {
        title: 'Analyze Model Metrics',
        description: 'Review precision, recall, F1 scores, and user feedback. Break down by segment. Where is it failing?',
        productSenseChallenge: 'Solution Development - Metrics analysis'
      },
      {
        title: 'Segment Performance Issues',
        description: 'Which user segments are struggling most? Long meetings? Non-English? Technical discussions?',
        productSenseChallenge: 'Segmentation - Performance analysis by segment'
      },
      {
        title: 'Identify Root Causes',
        description: 'Understand why the model is underperforming. Is it data quality? Prompt design? Model choice?',
        productSenseChallenge: 'Problem Identification - Root cause analysis'
      },
      {
        title: 'Prioritize Improvements',
        description: 'Use impact-effort analysis. Should we improve the model? Change prompts? Add features? What\'s the ROI?',
        productSenseChallenge: 'Solution Development - Prioritization with impact-effort'
      },
      {
        title: 'Balance Accuracy vs Speed vs Cost',
        description: 'Make trade-offs between model accuracy, response time, and API costs. What\'s the optimal balance?',
        productSenseChallenge: 'Solution Development - Trade-off analysis'
      },
      {
        title: 'Communicate to Stakeholders',
        description: 'Explain technical limitations to non-technical stakeholders. Set realistic expectations.',
        productSenseChallenge: 'Clear Communication - Technical communication to non-technical audience'
      }
    ],
    stakeholders: ['engineering-ml', 'data-scientist', 'leadership-ceo', 'customer-success'],
    productSenseChallenges: [
      'Solution Development: Metrics analysis and prioritization',
      'Segmentation: Performance analysis by segment',
      'Problem Identification: Root cause analysis',
      'Solution Development: Impact-effort prioritization and trade-off analysis',
      'Clear Communication: Technical communication to non-technical stakeholders'
    ],
    successCriteria: {
      impact: 'Model performance improves',
      feasibility: 'Improvements are realistic',
      stakeholderManagement: 'Stakeholders understand limitations',
      speed: 'Issues addressed promptly',
      frameworkApplication: 'Applied ML performance best practices'
    }
  }
};
